/**
 * Results Release Control + Release Preview Modal.
 *
 * Extracted from AdminApp.jsx (Feb 2026 modularisation pass). Owns the
 * full "preview → confirm → release" flow for Sales Cert scores. The
 * modal includes a client-side CSV export so L&D can circulate the
 * release list before firing emails.
 *
 * Two named exports:
 *   - ResultsReleaseCard: card rendered inside Overview when the admin
 *     is not in client-lead mode.
 *   - ReleasePreviewModal: modal rendered from the card when the admin
 *     clicks "Preview & release".
 */
import { useEffect, useState } from "react";
import { Download, Eye, Send, X } from "lucide-react";
import api, { formatApiError } from "../../api";
import { trackDownload } from "../../hooks/useActivityLogger";

// ---------------- Results Release Control (Feb 2026) ----------------
// Toggles whether Sales Cert candidates can see their scores + report +
// certificate. Admin only. Before release, participants see 'Recorded' /
// 'Submitted' messaging on all Sales Cert flows. Firing the release also
// dispatches Resend certificate/report-ready emails to every candidate who
// engaged with the assessment.
export function ResultsReleaseCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [flash, setFlash] = useState("");

  const load = () => api.get("/admin/salescert/release-state")
    .then((r) => setState(r.data))
    .catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openPreview = async () => {
    setBusy(true); setErr(""); setPreview(null);
    try {
      const r = await api.get("/admin/salescert/release-preview");
      setPreview(r.data);
      setPreviewing(true);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const release = async () => {
    setBusy(true); setErr(""); setFlash("");
    try {
      const r = await api.post("/admin/salescert/release", { send_emails: true });
      setState(r.data);
      setFlash(r.data.was_already_released
        ? "Already released — no new emails sent."
        : "Results released. Certificate + report emails are dispatching in the background.");
      setPreviewing(false);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const unrelease = async () => {
    if (!window.confirm("Hide all Sales Cert results from participants again? Emails already sent cannot be recalled.")) return;
    setBusy(true); setErr(""); setFlash("");
    try {
      const r = await api.post("/admin/salescert/unrelease", {});
      setState(r.data);
      setFlash("Results are now hidden from candidates again.");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!state) return null;
  const released = state.released;
  return (
    <div
      className={`p-6 border-l-4 ${released ? "border-[#10B981] bg-[#D1FAE5]/30" : "border-[#EE2737] bg-[#FEE2E2]/25"}`}
      data-testid="results-release-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="overline mb-1">RESULTS · CANDIDATE VISIBILITY</div>
          <div className="font-heading font-black text-lg leading-tight">
            {released
              ? "Scores + certificates are LIVE for candidates"
              : "Scores are HIDDEN from candidates"}
          </div>
          <div className="text-[12px] text-[#525252] mt-2 max-w-xl leading-relaxed">
            {released ? (
              <>
                Candidates now see their consolidated report and can download their certificates.
                Released on <b>{(state.released_at || "").slice(0, 16).replace("T", " ")}</b>
                {state.released_by_email ? <> by <b>{state.released_by_email}</b></> : null}.
                {state.email_sent_count > 0 && (
                  <> Emails delivered: <b>{state.email_sent_count}</b>
                    {state.email_failed_count ? <> · failed: <b>{state.email_failed_count}</b></> : null}.
                  </>
                )}
              </>
            ) : (
              <>
                Candidates only see "Recorded" on every section screen — no marks, no certified badge,
                no report page. You and VI L&D leads see everything as usual. Fire the release once you
                have finished moderating; it will email the certificate + report link to all
                {state.eligible_count ? <> ~<b>{state.eligible_count}</b></> : ""} eligible candidates
                via Resend.
              </>
            )}
          </div>
          {flash && <div className="mt-2 text-[11px] text-[#059669] font-semibold" data-testid="release-flash">{flash}</div>}
          {err && <div className="mt-2 text-[11px] text-[#EE2737]" data-testid="release-err">{err}</div>}
        </div>
        <div className="flex flex-col items-end gap-2">
          {!released && (
            <button
              onClick={openPreview}
              className="btn btn-primary !text-xs"
              disabled={busy}
              data-testid="release-results-btn"
            >
              <Eye size={13} /> {busy ? "Loading preview…" : "Preview & release"}
            </button>
          )}
          {released && (
            <button
              onClick={unrelease}
              className="btn btn-ghost !text-xs"
              disabled={busy}
              data-testid="unrelease-results-btn"
              title="Hide results from candidates again (emails already sent cannot be recalled)"
            >
              {busy ? "Hiding…" : "Un-release (hide again)"}
            </button>
          )}
        </div>
      </div>

      {previewing && preview && (
        <ReleasePreviewModal
          preview={preview}
          busy={busy}
          onConfirm={release}
          onClose={() => setPreviewing(false)}
        />
      )}
    </div>
  );
}


// ---------------- Release Preview Modal ----------------
// Shows the admin a one-page dry-run of who will certify vs miss the 60%
// threshold, plus which candidates never engaged, before they fire the
// email fan-out.
export function ReleasePreviewModal({ preview, busy, onConfirm, onClose }) {
  const s = preview.summary;
  const rows = preview.candidates || [];
  const statusChip = (row) => {
    if (row.status === "certifying") return <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-[#D1FAE5] text-[#065F46]">Certifying</span>;
    if (row.status === "missing") return <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-[#FEE2E2] text-[#991B1B]">Below 60%</span>;
    if (row.status === "incomplete") return <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-[#FEF3C7] text-[#92400E]">Incomplete</span>;
    return <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-[#F5F5F5] text-[#525252]">No-show</span>;
  };
  const cell = (marks, max) => marks === null || marks === undefined
    ? <span className="text-[#B0B0B0]">—</span>
    : <><b>{marks}</b><span className="text-[#878787]"> / {max}</span></>;

  // ------- CSV export -------
  // Client-side CSV so L&D can share the release preview around before
  // firing emails. Columns match the visible preview PLUS Employee ID +
  // Program Level (Ascend / Summit) which aren't rendered in the table
  // but are useful when circulating to programme leads.
  const statusLabel = {
    certifying: "Certifying",
    missing: "Below 60%",
    incomplete: "Incomplete",
    no_show: "No-show",
  };
  const csvEscape = (v) => {
    if (v === null || v === undefined) return "";
    const str = String(v);
    // RFC 4180 — wrap in quotes if it contains comma, quote, newline
    if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
  };
  const downloadCsv = () => {
    trackDownload("release_preview_csv", { total: s.total, engaged: s.engaged });
    const mcqMax = rows[0]?.mcq_max ?? 50;
    const header = [
      "Employee ID", "Name", "Email", "Program Level", "Role Level", "Region",
      `MCQ (of ${mcqMax})`, "MCQ Attempts",
      "Pitch (of 25)", "Role Play (of 25)",
      "Total", "Total Max", "Percentage", "Sections Complete", "Status",
    ];
    const body = rows.map((r) => [
      r.employee_id || "",
      r.name || "",
      r.email || "",
      r.program_level || "",
      r.role_level || "",
      r.region || "",
      r.mcq_marks ?? "",
      r.mcq_attempts_used || 0,
      r.pitch_marks ?? "",
      r.roleplay_marks ?? "",
      r.sections_complete === 0 ? "" : r.total_marks,
      r.total_max,
      r.sections_complete === 0 ? "" : `${r.pct}%`,
      r.sections_complete,
      statusLabel[r.status] || r.status,
    ]);
    // BOM so Excel opens UTF-8 correctly for names with accents
    const csv = "\ufeff" + [header, ...body].map((r) => r.map(csvEscape).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
    link.href = url;
    link.download = `salescert-release-preview_${ts}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="release-preview-modal"
    >
      <div className="bg-white max-w-6xl w-full max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0A0A0A] text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#EE2737]">RELEASE PREVIEW · READ-ONLY DRY RUN</div>
            <h2 className="font-heading font-black text-lg">Confirm before releasing to {s.engaged} engaged candidate{s.engaged === 1 ? "" : "s"}</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="close-release-preview"><X size={22} /></button>
        </div>

        {/* Summary strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-[#E5E7EB] border-b border-[#E5E7EB] shrink-0">
          <div className="bg-white p-4">
            <div className="overline mb-1">TOTAL</div>
            <div className="text-2xl font-heading font-black" data-testid="preview-total">{s.total}</div>
          </div>
          <div className="bg-white p-4">
            <div className="overline mb-1 !text-[#065F46]">CERTIFYING</div>
            <div className="text-2xl font-heading font-black text-[#065F46]" data-testid="preview-certifying">{s.certifying}</div>
            <div className="text-[10px] text-[#878787] mt-1">≥{s.threshold_pct}% of {s.total_max}</div>
          </div>
          <div className="bg-white p-4">
            <div className="overline mb-1 !text-[#991B1B]">BELOW 60%</div>
            <div className="text-2xl font-heading font-black text-[#991B1B]" data-testid="preview-missing">{s.missing}</div>
            <div className="text-[10px] text-[#878787] mt-1">All 3 done, under threshold</div>
          </div>
          <div className="bg-white p-4">
            <div className="overline mb-1 !text-[#92400E]">INCOMPLETE</div>
            <div className="text-2xl font-heading font-black text-[#92400E]" data-testid="preview-incomplete">{s.incomplete}</div>
            <div className="text-[10px] text-[#878787] mt-1">Started, didn&apos;t finish</div>
          </div>
          <div className="bg-white p-4">
            <div className="overline mb-1">NO-SHOW</div>
            <div className="text-2xl font-heading font-black text-[#525252]" data-testid="preview-noshow">{s.no_show}</div>
            <div className="text-[10px] text-[#878787] mt-1">No email will fire</div>
          </div>
        </div>

        {/* Scrollable table */}
        <div className="overflow-auto flex-1">
          <table className="w-full text-[12px]" data-testid="release-preview-table">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] sticky top-0">
              <tr className="text-left">
                <th className="px-4 py-3 overline">Candidate</th>
                <th className="px-3 py-3 overline text-right">MCQ /50</th>
                <th className="px-3 py-3 overline text-right">Pitch /25</th>
                <th className="px-3 py-3 overline text-right">Role-Play /25</th>
                <th className="px-3 py-3 overline text-right">Total</th>
                <th className="px-3 py-3 overline text-right">%</th>
                <th className="px-4 py-3 overline">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-[#F1F1F1] hover:bg-[#FAFAFA]" data-testid={`release-preview-row-${row.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{row.name}</div>
                    <div className="text-[10px] text-[#878787]">{row.email}{row.role_level ? ` · ${row.role_level}` : ""}{row.region ? ` · ${row.region}` : ""}</div>
                  </td>
                  <td className="px-3 py-3 text-right font-mono">
                    {cell(row.mcq_marks, row.mcq_max)}
                    {row.mcq_attempts_used > 1 && <div className="text-[9px] text-[#878787] uppercase tracking-widest">Best of {row.mcq_attempts_used}</div>}
                  </td>
                  <td className="px-3 py-3 text-right font-mono">{cell(row.pitch_marks, row.pitch_max)}</td>
                  <td className="px-3 py-3 text-right font-mono">{cell(row.roleplay_marks, row.roleplay_max)}</td>
                  <td className="px-3 py-3 text-right font-mono">
                    {row.sections_complete === 0
                      ? <span className="text-[#B0B0B0]">—</span>
                      : <><b>{row.total_marks}</b><span className="text-[#878787]"> / {row.total_max}</span></>}
                  </td>
                  <td className="px-3 py-3 text-right font-mono font-bold">
                    {row.sections_complete === 0 ? <span className="text-[#B0B0B0]">—</span> : `${row.pct}%`}
                  </td>
                  <td className="px-4 py-3">{statusChip(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer with confirm */}
        <div className="border-t border-[#E5E7EB] px-6 py-4 flex items-center justify-between gap-3 flex-wrap bg-white">
          <div className="text-[11px] text-[#525252] max-w-2xl leading-relaxed">
            Confirming will make scores + reports visible to <b>{s.engaged}</b> engaged candidate{s.engaged === 1 ? "" : "s"},
            attach a certificate PDF for the <b>{s.certifying}</b> who cleared 60%, and email everyone via Resend.
            {s.no_show > 0 && <> The <b>{s.no_show}</b> no-show{s.no_show === 1 ? "" : "s"} will not receive any email.</>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadCsv}
              className="btn btn-ghost !text-xs"
              disabled={rows.length === 0}
              data-testid="release-preview-download-csv"
              title="Download this preview as a CSV for circulation"
            >
              <Download size={13} /> Download CSV
            </button>
            <button
              onClick={onClose}
              className="btn btn-ghost !text-xs"
              disabled={busy}
              data-testid="release-preview-cancel"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="btn btn-primary !text-xs"
              disabled={busy || s.engaged === 0}
              data-testid="release-preview-confirm"
              title={s.engaged === 0 ? "Nothing to release — no candidates have engaged yet." : ""}
            >
              <Send size={13} /> {busy ? "Releasing…" : `Release to ${s.engaged} candidate${s.engaged === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


